/**
 * Pointer-driven 3D tilt with per-layer parallax. Layers are children of `stage`
 * marked with `data-depth` (positive = moves more). On touch devices, or when
 * reduced motion is requested, it falls back to a subtle scroll parallax / nothing.
 */
export function useTilt (
  stage: Ref<HTMLElement | null>,
  options: { maxTilt?: number, maxShift?: number } = {},
) {
  const { maxTilt = 10, maxShift = 24 } = options

  let frame = 0
  let target = { x: 0, y: 0 }
  const current = { x: 0, y: 0 }
  let removeListeners = () => {}

  function apply () {
    const el = stage.value
    if (!el) { return }
    current.x += (target.x - current.x) * 0.08
    current.y += (target.y - current.y) * 0.08

    el.style.transform = `rotateX(${(-current.y * maxTilt).toFixed(2)}deg) rotateY(${(current.x * maxTilt).toFixed(2)}deg)`
    el.querySelectorAll<HTMLElement>('[data-depth]').forEach((layer) => {
      const depth = Number(layer.dataset.depth)
      layer.style.transform = `translate3d(${(current.x * maxShift * depth).toFixed(2)}px, ${(current.y * maxShift * depth).toFixed(2)}px, 0)`
    })

    const settled = Math.abs(target.x - current.x) < 0.001 && Math.abs(target.y - current.y) < 0.001
    frame = settled ? 0 : requestAnimationFrame(apply)
  }

  function schedule () {
    if (!frame) { frame = requestAnimationFrame(apply) }
  }

  onMounted(() => {
    const el = stage.value
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return }

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      // Track the pointer across the whole hero so the tilt feels ambient
      const area = el.closest('section') ?? document.body
      const onMove = (event: PointerEvent) => {
        const rect = area.getBoundingClientRect()
        target = {
          x: ((event.clientX - rect.left) / rect.width - 0.5) * 2,
          y: ((event.clientY - rect.top) / rect.height - 0.5) * 2,
        }
        schedule()
      }
      const onLeave = () => {
        target = { x: 0, y: 0 }
        schedule()
      }
      area.addEventListener('pointermove', onMove as EventListener, { passive: true })
      area.addEventListener('pointerleave', onLeave)
      removeListeners = () => {
        area.removeEventListener('pointermove', onMove as EventListener)
        area.removeEventListener('pointerleave', onLeave)
      }
    } else {
      // Touch: depth-based parallax driven by scroll
      const onScroll = () => {
        target = { x: 0, y: Math.min(window.scrollY / 400, 1) * 0.8 }
        schedule()
      }
      window.addEventListener('scroll', onScroll, { passive: true })
      removeListeners = () => window.removeEventListener('scroll', onScroll)
    }
  })

  onBeforeUnmount(() => {
    removeListeners()
    cancelAnimationFrame(frame)
  })
}
